// Notification extraction utilities
// Parses feeds and yogas to generate notification objects

import type { 
  OpportunityNotification, 
  CautionNotification, 
  YogaNotification,
  PredictionNotification
} from '../components/NotificationCenter';
import type { DashaContext, TransitContext, HouseNum, PlanetName } from '../astro/feeds/types';
import type { FunctionalClassification } from '../astro/feeds/generateFeedsV2';

/**
 * Planet → body organs / life systems mapping (Vedic classical + medical astrology).
 * Mirrors the PLANET–ILLNESS MAPPING used in the AI chat context.
 */
export const PLANET_BODY_ORGANS: Partial<Record<PlanetName, string>> = {
  Sun:     'eyes, heart, vitality',
  Moon:    'mind, sleep, emotional health',
  Mars:    'blood, muscles, skin, inflammation',
  Mercury: 'nervous system, respiratory tract, speech',
  Jupiter: 'liver, lungs, fat metabolism',
  Venus:   'reproductive system, throat, kidneys',
  Saturn:  'bones, joints, chronic conditions',
  Rahu:    'mental health, unexplained ailments',
  Ketu:    'mysterious or misdiagnosed conditions',
};

/** All recognised planet names — used to extract active planets from feed message text */
const PLANET_NAMES: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

/** Extract planet names mentioned anywhere in a feed message */
function extractPlanetsFromMessage(message: string): PlanetName[] {
  return PLANET_NAMES.filter(p => message.includes(p));
}

/**
 * Build a compact organ watch string from a list of planet names.
 * e.g. [Saturn, Jupiter] → "bones, joints, chronic conditions (Saturn) · liver, lungs, fat metabolism (Jupiter)"
 * Returns empty string if no organs found.
 */
export function buildOrganWatchLine(planets: PlanetName[]): string {
  const unique = [...new Set(planets)];
  const parts = unique
    .map(p => PLANET_BODY_ORGANS[p] ? `${PLANET_BODY_ORGANS[p]} (${p})` : null)
    .filter(Boolean) as string[];
  return parts.join(' · ');
}

/**
 * Feed structure as generated by App.tsx reportFeeds
 */
export interface AppFeedItem {
  id: string;
  lifeArea: string;
  severity: 'positive' | 'mixed' | 'caution' | 'alert'; // Explicit severity classification
  score: number;
  message: string;
  shortMessage?: string;
  window?: {
    start: Date;
    end: Date;
  };
  evidence?: string[];
}

/**
 * Simplified dasha context for prediction functions
 * Uses direct property access instead of levels array
 */
export type SimplifiedDashaContext = {
  MD: { planet: PlanetName; level: 'MD'; rules?: HouseNum[]; occupies?: HouseNum; aspects?: HouseNum[] };
  AD: { planet: PlanetName; level: 'AD'; rules?: HouseNum[]; occupies?: HouseNum; aspects?: HouseNum[] };
  PD: { planet: PlanetName; level: 'PD'; rules?: HouseNum[]; occupies?: HouseNum; aspects?: HouseNum[] };
  SD: { planet: PlanetName; level: 'SD'; rules?: HouseNum[]; occupies?: HouseNum; aspects?: HouseNum[] };
  lagnaSign?: number;
  houseScores?: Record<HouseNum, number>;
};

/**
 * Enhanced context for sophisticated prediction generation
 * Phase 1-4 implementation
 * 
 * Simplified structure - doesn't require strict DashaContext tuple type
 */
export interface EnhancedPredictionContext {
  dashaContext: SimplifiedDashaContext;
  functionalClassification: FunctionalClassification;
  houseScores: Record<HouseNum, number>;
  transitContext: TransitContext;
  emptyHouses: HouseNum[];
  // Transit enrichment (wired from transitData in App.tsx)
  transitRetrogrades?: Partial<Record<string, boolean>>;
  transitDignities?: Partial<Record<string, string>>;
  transitCombust?: Partial<Record<string, boolean>>;
  moonTransitHouse?: number; // Current Moon transit house 1-12 (whole-sign)
}

/**
 * Extract opportunity notifications from positive feeds
 * Filters feeds with severity = 'positive', sorts by nearest start date
 */
export function extractOpportunities(feeds: AppFeedItem[]): OpportunityNotification[] {
  const now = new Date();
  
  return feeds
    .filter(feed => feed.severity === 'positive' && feed.window && feed.window.end > now) // Use severity classification
    .map(feed => ({
      id: feed.id || `opp-${feed.window?.start.getTime()}-${feed.lifeArea}`,
      type: 'opportunity' as const,
      title: feed.lifeArea === 'self' ? 'Health' : (feed.lifeArea.charAt(0).toUpperCase() + feed.lifeArea.slice(1)), // Change "self" to "Health"
      description: feed.shortMessage ?? feed.message,
      startDate: feed.window!.start,
      endDate: feed.window!.end,
      lifeArea: feed.lifeArea,
      severity: Math.abs(feed.score)
    }))
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    .slice(0, 10); // Limit to 10 most immediate
}

/**
 * Extract caution notifications from negative feeds.
 * Health/self-area cautions are automatically enriched with an organWatch field
 * derived from planet names found in the feed message — completely separate from
 * the description parsers so existing extractCause/extractEffect/extractAction
 * logic is unaffected.
 */
export function extractCautions(feeds: AppFeedItem[]): CautionNotification[] {
  const now = new Date();

  return feeds
    .filter(feed => (feed.severity === 'caution' || feed.severity === 'alert') && feed.window && feed.window.end > now)
    .map(feed => {
      const isHealthArea = feed.lifeArea === 'self' || feed.lifeArea === 'health';
      const organWatch = isHealthArea
        ? buildOrganWatchLine(extractPlanetsFromMessage(feed.message || '')) || undefined
        : undefined;

      return {
        id: feed.id || `caution-${feed.window?.start.getTime()}-${feed.lifeArea}`,
        type: 'caution' as const,
        title: feed.lifeArea === 'self' ? 'Health' : (feed.lifeArea.charAt(0).toUpperCase() + feed.lifeArea.slice(1)),
        description: feed.shortMessage ?? feed.message,
        startDate: feed.window!.start,
        endDate: feed.window!.end,
        lifeArea: feed.lifeArea,
        severity: Math.abs(feed.score),
        organWatch,
      };
    })
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    .slice(0, 10);
}

/**
 * Extract active yoga notifications
 * Filters yogas that are currently active or starting soon
 */
export interface YogaData {
  id: string;
  type: string;
  name: string;
  description?: string;
  active_from: string | null;
  active_to: string | null;
  is_permanent: boolean;
}

export function extractActiveYogas(yogas: YogaData[]): YogaNotification[] {
  const now = new Date();
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  
  return yogas
    .filter(yoga => {
      if (yoga.is_permanent) return true;
      if (!yoga.active_from) return false;
      
      const startDate = new Date(yoga.active_from);
      return startDate <= sevenDaysFromNow;
    })
    .map(yoga => ({
      id: yoga.id,
      type: 'yoga' as const,
      name: yoga.name || yoga.type,
      description: yoga.description || `${yoga.type} yoga detected in your chart`,
      startDate: yoga.active_from ? new Date(yoga.active_from) : now,
      endDate: yoga.active_to ? new Date(yoga.active_to) : new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000),
      isPermanent: yoga.is_permanent
    }))
    .sort((a, b) => {
      if (a.isPermanent && !b.isPermanent) return 1;
      if (!a.isPermanent && b.isPermanent) return -1;
      return a.startDate.getTime() - b.startDate.getTime();
    })
    .slice(0, 8); // Limit to 8 most relevant
}

// ============= PHASE 1-4: ENHANCED PREDICTION LOGIC =============

/**
 * Phase 2: Get dasha level weights for specific timeframe
 * Daily: SD triggers events, PD enables
 * Weekly: AD+PD themes dominate
 * Monthly: AD strategic planning
 */
function getTimeframeDashaWeights(period: 'today' | 'week' | 'month'): {
  MD: number, AD: number, PD: number, SD: number
} {
  switch (period) {
    case 'today':
      return { MD: 0, AD: 0.2, PD: 0.3, SD: 1.0 }; // SD is the event trigger
    case 'week':
      return { MD: 0, AD: 0.5, PD: 1.0, SD: 0.7 }; // AD+PD themes dominate
    case 'month':
      return { MD: 0.3, AD: 1.0, PD: 0.6, SD: 0.4 }; // AD strategic planning
  }
}

/**
 * Phase 2: Check if Rahu/Ketu period (event trigger)
 */
function isEventTriggerPeriod(dashaContext: SimplifiedDashaContext): boolean {
  return dashaContext.SD.planet === 'Rahu' || 
         dashaContext.SD.planet === 'Ketu' ||
         dashaContext.PD.planet === 'Rahu' ||
         dashaContext.PD.planet === 'Ketu';
}

/**
 * Phase 1: Get active houses for a dasha level
 * Returns empty array if required properties not available
 */
function getActiveHouses(level: { rules?: HouseNum[]; occupies?: HouseNum; aspects?: HouseNum[] }): HouseNum[] {
  const houses = new Set<HouseNum>();
  if (level.rules) level.rules.forEach(h => houses.add(h));
  if (level.occupies) houses.add(level.occupies);
  if (level.aspects) level.aspects.forEach(h => houses.add(h));
  return Array.from(houses);
}

/**
 * Phase 1: Analyze empty house with malefic aspects
 * Returns vulnerability score (negative = challenging)
 */
function analyzeEmptyHouseVulnerability(
  houseNum: HouseNum,
  dashaContext: SimplifiedDashaContext,
  functionalClassification: FunctionalClassification,
  emptyHouses: HouseNum[]
): number {
  if (!emptyHouses.includes(houseNum)) return 0;
  
  let vulnerabilityScore = 0;
  
  // Check all dasha levels for aspects to this house
  [dashaContext.SD, dashaContext.PD, dashaContext.AD].forEach(level => {
    const activeHouses = getActiveHouses(level);
    
    if (activeHouses.includes(houseNum)) {
      const classification = functionalClassification[level.planet] || 'neutral';
      
      if (classification === 'malefic') {
        // Malefic activating empty house = significant vulnerability
        vulnerabilityScore -= 25;
      } else if (classification === 'benefic') {
        // Benefic can help cushion empty house
        vulnerabilityScore += 10;
      }
    }
  });
  
  return vulnerabilityScore;
}

/**
 * Phase 1: Compute transit alignment with dasha themes
 * Positive = reinforcement, Negative = contradiction
 */
function computeTransitAlignment(
  dashaContext: SimplifiedDashaContext,
  transitContext: TransitContext,
  functionalClassification: FunctionalClassification,
  timeframe: 'today' | 'week' | 'month'
): number {
  // For daily: focus on SD, for weekly: PD, for monthly: AD
  const primaryLevel = timeframe === 'today' ? dashaContext.SD :
                      timeframe === 'week' ? dashaContext.PD :
                      dashaContext.AD;
  
  const dashaHouses = getActiveHouses(primaryLevel);
  let alignmentScore = 0;
  
  dashaHouses.forEach(house => {
    // Check transiting planets in this house
    transitContext.positions.forEach(transitPos => {
      if (transitPos.house === house) {
        const planet = transitPos.planet;
        const transitNature = functionalClassification[planet];
        const dashaNature = functionalClassification[primaryLevel.planet];
        
        // Reinforcement: same functional nature
        if (transitNature === dashaNature) {
          alignmentScore += 15;
        }
        // Contradiction: opposite natures create confusion
        else if (
          (transitNature === 'malefic' && dashaNature === 'benefic') ||
          (transitNature === 'benefic' && dashaNature === 'malefic')
        ) {
          alignmentScore -= 20;
        }
        
        // Yoga karaka bonus
        if (transitNature === 'yoga_karaka') {
          alignmentScore += 10;
        }
      }
    });
  });
  
  return alignmentScore;
}

/**
 * Phase 3: Calculate weighted magnitude score for prediction
 */
function calculatePredictionScore(
  feeds: AppFeedItem[],
  context: EnhancedPredictionContext | null,
  timeframe: 'today' | 'week' | 'month'
): {
  score: number;
  confidence: number;
  primaryAreas: string[];
  tone: 'very_favorable' | 'favorable' | 'mixed' | 'challenging' | 'very_challenging';
  details: string[];
  cautionAreas: Set<string>;
} {
  const feedScoresSummary = feeds.map(f => ({ lifeArea: f.lifeArea, score: f.score }));
  const avgInputScore = feeds.reduce((sum, f) => sum + f.score, 0) / feeds.length;
  console.log('[calculatePredictionScore]', {
    timeframe,
    hasContext: !!context,
    feedCount: feeds.length,
    avgInputScore: avgInputScore.toFixed(2),
    scoreRange: {
      min: Math.min(...feeds.map(f => f.score)),
      max: Math.max(...feeds.map(f => f.score))
    },
    feedScores: feedScoresSummary.slice(0, 10) // Show first 10 only
  });
  
  if (!context) {
    // Fallback to simple logic if no context (shouldn't happen normally)
    console.warn('[calculatePredictionScore] No context provided, using fallback logic');
    const positiveCount = feeds.filter(f => f.score > 0).length;
    const negativeCount = feeds.filter(f => f.score < 0).length;
    const avgScore = feeds.length > 0 ? feeds.reduce((sum, f) => sum + f.score, 0) / feeds.length : 0;
    
    return {
      score: avgScore,
      confidence: 50,
      primaryAreas: Array.from(new Set(feeds.map(f => f.lifeArea))).slice(0, 3),
      tone: positiveCount > negativeCount ? 'favorable' : negativeCount > positiveCount ? 'challenging' : 'mixed',
      details: [],
      cautionAreas: new Set() // No caution flags in fallback mode
    };
  }
  
  const { dashaContext, functionalClassification, houseScores, transitContext, emptyHouses } = context;
  const weights = getTimeframeDashaWeights(timeframe);
  
  let totalScore = 0;
  let weightSum = 0;
  const details: string[] = [];
  
  // Import natural planet natures
  const PLANET_NATURE: Record<PlanetName, 'benefic'|'malefic'|'conditional'> = {
    Jupiter:'benefic', Venus:'benefic',
    Mercury:'conditional', Moon:'conditional',
    Sun:'malefic', Mars:'malefic', Saturn:'malefic',
    Rahu:'malefic', Ketu:'malefic'
  };
  
  // Phase 1: Analyze natural + functional classification of SD/PD/AD
  // Apply penalty ONLY if there's a mismatch indicating extreme difficulty
  const sdPlanet = dashaContext.SD.planet;
  const pdPlanet = dashaContext.PD.planet;
  const adPlanet = dashaContext.AD.planet;
  const sdFunctional = functionalClassification[sdPlanet];
  const pdFunctional = functionalClassification[pdPlanet];
  const adFunctional = functionalClassification[adPlanet];
  const sdNatural = PLANET_NATURE[sdPlanet];
  const pdNatural = PLANET_NATURE[pdPlanet];
  const adNatural = PLANET_NATURE[adPlanet];
  
  let combinedPenalty = 1.0;
  
  // AD: Check for context only (no penalty applied, just logging)
  if (adFunctional === 'malefic' && adNatural === 'malefic') {
    details.push(`${adPlanet} AD is both functional & natural malefic - underlying theme challenging (context only)`);
  }
  
  // SD: Double malefic = stronger penalty
  if (sdFunctional === 'malefic' && sdNatural === 'malefic') {
    combinedPenalty *= 0.85; // -15% for double malefic
    details.push(`${sdPlanet} SD is both functional & natural malefic - increased difficulty`);
  } else if (sdFunctional === 'malefic' && sdNatural === 'benefic') {
    combinedPenalty *= 0.95; // -5% lesser penalty (natural benefic cushions)
    details.push(`${sdPlanet} SD functional malefic but natural benefic - moderate difficulty`);
  }
  
  // PD: Double malefic = moderate penalty (lower weight than SD)
  if (pdFunctional === 'malefic' && pdNatural === 'malefic') {
    combinedPenalty *= 0.92; // -8% for PD double malefic
    details.push(`${pdPlanet} PD is both functional & natural malefic - challenges present`);
  } else if (pdFunctional === 'malefic' && pdNatural === 'benefic') {
    combinedPenalty *= 0.97; // -3% lesser penalty
  }
  
  // Flag if SD or PD has double malefic
  const hasDoubleeMalefic = (sdFunctional === 'malefic' && sdNatural === 'malefic') ||
                            (pdFunctional === 'malefic' && pdNatural === 'malefic');
  
  // Flag if SD or PD has single malefic penalty (functional malefic + natural benefic)
  const hasSingleMalefic = (sdFunctional === 'malefic' && sdNatural === 'benefic') ||
                           (pdFunctional === 'malefic' && pdNatural === 'benefic');
  
  // Check for health vulnerability pattern: Natural malefic in PD/SD aspecting H6 + Rahu/Ketu in AD
  const adNaturalMalefic = adNatural === 'malefic';
  const pdIsNaturalMalefic = pdNatural === 'malefic';
  const sdIsNaturalMalefic = sdNatural === 'malefic';
  
  // Check if PD or SD aspects house 6 (health issues)
  const pdAspectsH6 = dashaContext.PD.aspects?.includes(6 as HouseNum) || false;
  const sdAspectsH6 = dashaContext.SD.aspects?.includes(6 as HouseNum) || false;
  
  // Track areas with caution flags
  const cautionAreas = new Set<string>();
  
  // Health vulnerability: Natural malefic (PD/SD) aspects H6 + Rahu/Ketu AD
  if ((pdIsNaturalMalefic && pdAspectsH6) || (sdIsNaturalMalefic && sdAspectsH6)) {
    cautionAreas.add('Health');
    if (adNaturalMalefic) {
      details.push(`Health vulnerability: ${pdIsNaturalMalefic && pdAspectsH6 ? pdPlanet + ' PD' : sdPlanet + ' SD'} (natural malefic) aspects H6 with ${adPlanet} AD amplifying`);
    }
  }
  
  // Process each feed (NO double counting - feeds already have functional classification)
  feeds.forEach(feed => {
    let feedScore = feed.score;
    
    // Apply ONLY the combined natural+functional penalty
    feedScore *= combinedPenalty;
    
    // Check if feed relates to empty house
    const feedLifeArea = feed.lifeArea;
    let emptyHouseModifier = 0;
    
    // Map common life areas to houses for empty house check
    const lifeAreaToHouses: Record<string, HouseNum[]> = {
      'health': [1, 6],
      'self': [1, 6],
      'wealth': [2, 11],
      'siblings': [3, 11],
      'mother': [4],
      'children': [5],
      'enemies': [6],
      'marriage': [7],
      'longevity': [8],
      'father': [9],
      'career': [10],
      'gains': [11],
      'losses': [12]
    };
    
    const relevantHouses = lifeAreaToHouses[feedLifeArea.toLowerCase()] || [];
    relevantHouses.forEach(house => {
      const vulnerability = analyzeEmptyHouseVulnerability(
        house,
        dashaContext,
        functionalClassification,
        emptyHouses
      );
      if (vulnerability !== 0) {
        emptyHouseModifier += vulnerability;
        if (vulnerability < 0) {
          details.push(`Empty house ${house} (${feedLifeArea}) with malefic aspects - increased vulnerability`);
          // Flag this area for caution override
          const displayArea = feedLifeArea === 'Self' ? 'Health' : feedLifeArea;
          cautionAreas.add(displayArea);
        }
      }
    });
    
    // Also flag area only if this specific area's houses are activated by malefic AND the feed itself is weak
    // (Do NOT blanket-flag all areas just because SD/PD is a malefic — that's too broad)
    if ((hasDoubleeMalefic || hasSingleMalefic) && feedScore < 0) {
      const displayArea = feedLifeArea === 'Self' ? 'Health' : feedLifeArea;
      cautionAreas.add(displayArea);
    }
    
    feedScore += emptyHouseModifier;
    
    // Accumulate
    totalScore += feedScore;
    weightSum += 1;
  });
  
  // Phase 2: Rahu/Ketu amplification (NOT double counted - this is additional context)
  if (isEventTriggerPeriod(dashaContext)) {
    totalScore *= 1.4;
    details.push(`Rahu/Ketu period amplifies event likelihood (×1.4)`);
  }
  
  // Wire computeTransitAlignment: adjust score based on transit-dasha agreement
  const transitAlignScore = computeTransitAlignment(dashaContext, transitContext, functionalClassification, timeframe);
  if (Math.abs(transitAlignScore) > 5) {
    totalScore += transitAlignScore * 0.3;
    details.push(`Transit ${transitAlignScore > 0 ? 'reinforces' : 'contradicts'} current dasha themes`);
  }

  const avgScore = weightSum > 0 ? totalScore / weightSum : 0;

  const perAreaScores = new Map<string, number[]>();
  feeds.forEach(f => {
    const displayArea = f.lifeArea === 'Self' ? 'Health' : f.lifeArea;
    if (!perAreaScores.has(displayArea)) {
      perAreaScores.set(displayArea, []);
    }
    perAreaScores.get(displayArea)!.push(f.score);
  });
  
  // Find worst area score to adjust tone
  let worstAreaScore = Infinity;
  perAreaScores.forEach(scores => {
    const areaAvg = scores.reduce((sum, s) => sum + s, 0) / scores.length;
    if (areaAvg < worstAreaScore) {
      worstAreaScore = areaAvg;
    }
  });
  
  // Count how many areas are positive vs negative — use majority voting for tone
  let negAreaCount = 0;
  let posAreaCount = 0;
  perAreaScores.forEach(scores => {
    const areaAvg = scores.reduce((sum, s) => sum + s, 0) / scores.length;
    if (areaAvg < 5) negAreaCount++;
    else posAreaCount++;
  });
  const majorityPositive = posAreaCount > negAreaCount;

  // Determine tone: only say "Difficult" when MOST areas are actually negative
  let tone: 'very_favorable' | 'favorable' | 'mixed' | 'challenging' | 'very_challenging';

  if (!majorityPositive && worstAreaScore < -20) {
    // Most areas negative + at least one severely negative
    tone = 'very_challenging';
  } else if (!majorityPositive && worstAreaScore < 0) {
    // Most areas negative
    tone = 'challenging';
  } else if (worstAreaScore < 0 && avgScore < 25) {
    // Some areas negative but majority positive — mixed overall
    tone = 'mixed';
  } else {
    // Majority of areas positive — use average score for nuance
    if (avgScore > 55) tone = 'very_favorable';
    else if (avgScore > 40) tone = 'favorable';
    else if (avgScore > 25) tone = 'mixed';
    else if (avgScore > 10) tone = 'challenging';
    else tone = 'very_challenging';
  }
  
  console.log('[calculatePredictionScore RESULT]', {
    timeframe,
    totalScore: totalScore.toFixed(2),
    avgScore: avgScore.toFixed(2),
    tone,
    combinedPenalty: combinedPenalty.toFixed(2),
    detailsCount: details.length
  });
  
  // Extract primary life areas
  const areaScores = new Map<string, number>();
  feeds.forEach(f => {
    const displayArea = f.lifeArea === 'Self' ? 'Health' : f.lifeArea; // Change "Self" to "Health"
    areaScores.set(displayArea, (areaScores.get(displayArea) || 0) + Math.abs(f.score));
  });
  const primaryAreas = Array.from(areaScores.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([area]) => area)
    .slice(0, 3);
  
  return {
    score: avgScore, // Use raw score
    confidence: Math.min(100, Math.abs(avgScore) + 30), // Higher base confidence
    primaryAreas,
    tone,
    details,
    cautionAreas // Return caution flags
  };
}

/**
 * Phase 4: Generate daily, weekly, and monthly predictions with enhanced logic
 * Analyzes feeds within each time window using sophisticated scoring
 */
export function generatePredictions(
  feeds: AppFeedItem[],
  context?: EnhancedPredictionContext | null
): PredictionNotification[] {
  const now = new Date();
  const predictions: PredictionNotification[] = [];
  
  // Today's prediction
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  
  const todayFeeds = feeds.filter(feed => 
    feed.window && 
    feed.window.start <= todayEnd && 
    feed.window.end >= now
  );
  
  if (todayFeeds.length > 0) {
    const result = calculatePredictionScore(todayFeeds, context || null, 'today');
    
    // Phase 4: Generate evidence-based summary that matches the calculated tone
    let summary = '';
    const { primaryAreas, cautionAreas } = result;
    
    if (context) {
      // Per-area actionable narrative sentences — descriptive and aligned with chip labels below.
      // Moon transit hint takes priority over the generic narrative for its matching area.
      const moonAreaHintMap: Record<number, string> = {
        1:  'Emotionally sensitive day — self-care and health decisions get better traction than usual.',
        2:  'Good day for financial conversations, reviewing expenses, or raising an income topic.',
        3:  'Favour direct one-to-one communication and short tasks over long planning sessions.',
        4:  'Family and home decisions are easier to progress today — a good day to raise pending matters.',
        5:  'Creative thinking is sharper — act on ideas and pitches, but avoid speculative financial bets.',
        6:  'Energy levels may dip — protect your health routine and avoid adding new workplace friction.',
        7:  'Good day to initiate or advance one-on-one conversations, negotiations, or resolve a disagreement.',
        8:  'Avoid impulsive financial decisions — introspection and research work better than action today.',
        9:  'Planning and learning moves faster than execution — good for research, proposals, and strategy.',
        10: 'Career visibility is higher today — a good day to put asks forward or make your work visible.',
        11: 'Collaborative and social outreach lands better than solo effort today — reach out to your network.',
        12: 'Mental fatigue is higher — protect focus time and avoid taking on draining obligations.',
      };
      // Houses 1/6 → Health, 2/8 → Wealth, 10/11 → Career (their hint becomes the area's summary line)
      const moonPriorityAreaMap: Record<number, string> = { 1: 'Health', 6: 'Health', 2: 'Wealth', 8: 'Wealth', 10: 'Career', 11: 'Career' };
      const moonH = context.moonTransitHouse ?? 0;
      const moonMappedArea = moonPriorityAreaMap[moonH] ?? '';
      let moonUsedForArea = '';

      // Color prefix per line: [g]=good(green), [m]=mixed(amber), [c]=caution/challenging(red), [n]=neutral context
      const avgToPrefix = (avg: number): string => avg > 25 ? '[g]' : avg > 5 ? '[m]' : '[c]';

      const getAreaNarrative = (area: string, avg: number): string => {
        const tier = avg > 50 ? 0 : avg > 25 ? 1 : avg > 5 ? 2 : avg > -15 ? 3 : 4;
        const narratives: Record<string, string[]> = {
          Career: [
            'Career is strongly supported today — ideal for asks, negotiations, and making your work visible.',
            'Career visibility is higher today — a good day to put asks forward or make your work visible.',
            'Career has mixed signals today — advance routine tasks but hold off on major career moves.',
            'Career faces some resistance today — complete existing work before taking on fresh initiatives.',
            'Career headwinds today — hold off on new commitments and stay low-key.',
          ],
          Wealth: [
            'Financial decisions are strongly supported today — good for investments, income conversations, and wealth moves.',
            'Wealth matters flow smoothly today — a good day for financial tasks or raising money topics.',
            'Wealth signals are mixed today — routine finances are fine, but avoid large new commitments.',
            'Some financial resistance today — defer major purchases or investment decisions if possible.',
            'Financial caution warranted today — hold off on large money moves or new commitments.',
          ],
          Health: [
            'Health and vitality are strong today — good time for physical effort and wellness activities.',
            'Health energy is steady today — maintain your routine and avoid overextending.',
            'Health signals are mixed today — protect your routine and avoid adding new physical stressors.',
            'Health needs some care today — reduce demands and don\'t push limits.',
            'Health needs attention today — rest, reduce stress, and avoid starting new regimens.',
          ],
        };
        return narratives[area]?.[tier] ?? `${area}: check highlights below`;
      };

      const ALL_PRIORITY_AREAS = ['Career', 'Wealth', 'Health'];
      const areaSentences: string[] = [];
      ALL_PRIORITY_AREAS.forEach(area => {
        const aFeeds = todayFeeds.filter(f => (f.lifeArea === 'Self' ? 'Health' : f.lifeArea) === area);
        if (aFeeds.length === 0) return;
        const avg = aFeeds.reduce((s, f) => s + f.score, 0) / aFeeds.length;
        if (area === moonMappedArea && moonAreaHintMap[moonH]) {
          areaSentences.push(avgToPrefix(avg) + moonAreaHintMap[moonH]);
          moonUsedForArea = area;
        } else {
          areaSentences.push(avgToPrefix(avg) + getAreaNarrative(area, avg));
        }
        // Append organ watch only when Health is mixed or worse (avg ≤ 25)
        // avg > 25 = Good/Steady → no watch needed
        if (area === 'Health' && avg <= 25) {
          const activePlanets = [
            context.dashaContext.SD?.planet,
            context.dashaContext.PD?.planet,
          ].filter((p): p is PlanetName => !!p);
          const organLine = buildOrganWatchLine(activePlanets);
          if (organLine) areaSentences.push(`[n]⚕️ Watch: ${organLine}`);
        }
      });
      summary = areaSentences.join('\n');

      // If no priority area feeds, fall back to a generic line based on primaryAreas
      if (areaSentences.length === 0 && primaryAreas.length > 0) {
        summary = primaryAreas.slice(0, 2).map(a => `[n]${a}: Check highlights below`).join('\n');
      }

      // Context addons (Moon transit, combust, retrograde) — appended as a separate line
      let contextLine = '';
      // Moon transit hint — only add to context line if NOT already used as a per-area summary sentence above.
      // Priority areas (Career/Wealth/Health) absorb their house hint directly into the summary.
      if (context.moonTransitHouse && !moonUsedForArea) {
        // Map house → life area keywords to gate the hint on relevant feeds existing
        const houseAreaMap: Record<number, string[]> = {
          1: ['Health'],
          2: ['Wealth', 'Finance'],
          3: ['Communication', 'Travel'],
          4: ['Family', 'Property', 'Home'],
          5: ['Children', 'Creativity', 'Romance'],
          6: ['Health', 'Work'],
          7: ['Relationship', 'Partnership', 'Marriage'],
          8: ['Wealth', 'Transformation'],
          9: ['Spirituality', 'Travel', 'Education'],
          10: ['Career'],
          11: ['Career', 'Social'],
          12: ['Spirituality', 'Travel'],
        };
        const h = context.moonTransitHouse;
        const relevantAreas = houseAreaMap[h] || [];
        const hasMatchingFeed = relevantAreas.length === 0 || relevantAreas.some(area =>
          todayFeeds.some(f => {
            const la = (f.lifeArea === 'Self' ? 'Health' : f.lifeArea) as string;
            return la.toLowerCase().includes(area.toLowerCase()) || area.toLowerCase().includes(la.toLowerCase());
          })
        );
        const moonHint = moonAreaHintMap[h];
        if (moonHint && hasMatchingFeed) contextLine += (contextLine ? ' ' : '') + moonHint;
      }

      // Retrograde warnings
      if (context.transitRetrogrades) {
        const retros = Object.entries(context.transitRetrogrades)
          .filter(([_, isR]) => isR).map(([p]) => p);
        if (retros.includes('Mercury')) contextLine += (contextLine ? ' ' : '') + 'Mercury retrograde — double-check messages and contracts.';
        else if (retros.includes('Saturn')) contextLine += (contextLine ? ' ' : '') + 'Saturn retrograde — review long-term plans before committing.';
        else if (retros.includes('Jupiter')) contextLine += (contextLine ? ' ' : '') + 'Jupiter retrograde — revisit growth strategies.';
      }

      // Transit dignity & combust context
      if (context.transitDignities) {
        const dignities = context.transitDignities as Record<string, string>;
        if (dignities.Jupiter === 'exalted') contextLine += (contextLine ? ' ' : '') + 'Jupiter exalted in transit — expansion and optimism amplified.';
        else if (dignities.Jupiter === 'debilitated') contextLine += (contextLine ? ' ' : '') + 'Jupiter debilitated in transit — growth slower; avoid over-committing.';
        if (dignities.Venus === 'exalted') contextLine += (contextLine ? ' ' : '') + 'Venus exalted — relationships and finances well-supported today.';
        else if (dignities.Venus === 'debilitated') contextLine += (contextLine ? ' ' : '') + 'Venus debilitated — keep relationship expectations in check.';
        if (dignities.Mercury === 'exalted') contextLine += (contextLine ? ' ' : '') + 'Mercury exalted — sharp thinking; good for decisions and communication.';
        else if (dignities.Mercury === 'debilitated') contextLine += (contextLine ? ' ' : '') + 'Mercury debilitated — re-read before sending; avoid complex negotiations.';
        if (dignities.Saturn === 'exalted') contextLine += (contextLine ? ' ' : '') + 'Saturn exalted — disciplined effort brings steady returns.';
        else if (dignities.Saturn === 'debilitated') contextLine += (contextLine ? ' ' : '') + 'Saturn debilitated — delays more likely; extra patience needed.';
        if (dignities.Mars === 'exalted') contextLine += (contextLine ? ' ' : '') + 'Mars exalted — drive and energy peak; good day for physical effort.';
        else if (dignities.Mars === 'debilitated') contextLine += (contextLine ? ' ' : '') + 'Mars debilitated — temper risk higher; channel energy carefully.';
      }
      if (context.transitCombust) {
        const combust = context.transitCombust as Record<string, boolean>;
        if (combust.Mercury) contextLine += (contextLine ? ' ' : '') + 'Mercury combust — re-read emails before sending.';
        if (combust.Venus) contextLine += (contextLine ? ' ' : '') + 'Venus combust — postpone difficult relationship or financial conversations.';
        if (combust.Mars) contextLine += (contextLine ? ' ' : '') + 'Mars combust — channel effort into solo focused work rather than group dynamics.';
        if (combust.Jupiter) contextLine += (contextLine ? ' ' : '') + 'Jupiter combust — defer large spending or investment decisions.';
        if (combust.Saturn) {
          const saturnNature = context.functionalClassification?.Saturn ?? 'neutral';
          if (saturnNature === 'malefic') {
            contextLine += (contextLine ? ' ' : '') + 'Saturn combust — karmic delays amplified; hold off on confrontations with authority.';
          } else if (saturnNature === 'yoga_karaka' || saturnNature === 'benefic') {
            contextLine += (contextLine ? ' ' : '') + 'Saturn combust — structured effort is less effective than usual; avoid overcommitting on long-term obligations.';
          } else {
            contextLine += (contextLine ? ' ' : '') + 'Saturn combust — authority and workplace friction elevated; avoid escalating issues with bosses.';
          }
        }
      }

      if (isEventTriggerPeriod(context.dashaContext)) {
        contextLine += (contextLine ? ' ' : '') + 'High-significance period — events today may have lasting impact.';
      }

      // Append context line as a separate line if non-empty — always neutral color
      if (contextLine) summary += '\n[n]' + contextLine;
    } else {
      // Fallback without context
      const positiveCount = todayFeeds.filter(f => f.score > 0).length;
      const negativeCount = todayFeeds.filter(f => f.score < 0).length;

      if (positiveCount > negativeCount * 1.5) {
        summary = `Favorable day ahead with ${positiveCount} positive influence${positiveCount > 1 ? 's' : ''}.`;
      } else if (negativeCount > positiveCount * 1.5) {
        summary = `Exercise caution today with ${negativeCount} challenging influence${negativeCount > 1 ? 's' : ''}.`;
      } else {
        summary = 'Mixed energies today. Balance and awareness are key.';
      }
    }
    
    // Score delta: compare today's avg score to yesterday's feeds (feeds active yesterday but not starting today)
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const yesterdayFeeds = feeds.filter(feed =>
      feed.window &&
      feed.window.start <= yesterday &&
      feed.window.end >= yesterday
    );
    let scoreDelta: number | undefined;
    if (yesterdayFeeds.length > 0) {
      const todayAvg = todayFeeds.reduce((sum, f) => sum + f.score, 0) / todayFeeds.length;
      const yesterdayAvg = yesterdayFeeds.reduce((sum, f) => sum + f.score, 0) / yesterdayFeeds.length;
      scoreDelta = Math.round(todayAvg - yesterdayAvg);
    }

    predictions.push({
      id: 'pred-today',
      type: 'prediction',
      period: 'today',
      title: 'Today\'s Forecast',
      summary,
      scoreDelta,
      highlights: (() => {
        // Career, Wealth, Health first; then other top areas
        const PRIORITY_AREAS = ['Career', 'Wealth', 'Health'];
        const allAreas = [
          ...PRIORITY_AREAS.filter(a => todayFeeds.some(f => (f.lifeArea === 'Self' ? 'Health' : f.lifeArea) === a)),
          ...primaryAreas.filter(a => !PRIORITY_AREAS.includes(a))
        ].slice(0, 4);

        return allAreas.map(area => {
          const areaFeeds = todayFeeds.filter(f => (f.lifeArea === 'Self' ? 'Health' : f.lifeArea) === area);
          if (areaFeeds.length === 0) return null;
          const avgScore = areaFeeds.reduce((sum, f) => sum + f.score, 0) / areaFeeds.length;
          const areaLabel = area === 'Self' ? 'Health' : (area.charAt(0).toUpperCase() + area.slice(1));

          // Area-specific action text per score tier
          const chipActions: Record<string, string[]> = {
            Career: [
              'push forward, make your work visible',
              'act on opportunities, put asks forward',
              'advance routine tasks only',
              'complete existing work first',
              'avoid new career initiatives',
            ],
            Wealth: [
              'strong for investments and income conversations',
              'handle financial tasks, raise money topics',
              'routine finances ok, no large commitments',
              'defer major financial decisions',
              'hold off on new financial moves',
            ],
            Health: [
              'good for physical effort and wellness',
              'maintain routine, avoid overextending',
              'protect routine, avoid new stressors',
              'rest and reduce demands',
              'rest, avoid new demands',
            ],
          };
          const chipTier = avgScore > 50 ? 0 : avgScore > 25 ? 1 : avgScore > 5 ? 2 : avgScore > -15 ? 3 : 4;
          const chipActionList = chipActions[areaLabel] ?? ['act now', 'make progress', 'time your moves', 'proceed carefully', 'hold off'];
          const chipAction = chipActionList[chipTier];
          if (avgScore > 50) return `${areaLabel}: Go ahead — ${chipAction}`;
          if (avgScore > 25) return `${areaLabel}: Good energy — ${chipAction}`;
          if (avgScore > 5)  return `${areaLabel}: Mixed — ${chipAction}`;
          if (avgScore > -15) return `${areaLabel}: Proceed carefully — ${chipAction}`;
          return `${areaLabel}: Hold off — ${chipAction}`;
        }).filter(Boolean) as string[];
      })()
    });
  }
  
  // This week's prediction
  const weekEnd = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const weekFeeds = feeds.filter(feed =>
    feed.window &&
    feed.window.start <= weekEnd &&
    feed.window.end >= now
  );
  
  if (weekFeeds.length > 0) {
    const result = calculatePredictionScore(weekFeeds, context || null, 'week');
    
    let summary = '';
    const { tone, primaryAreas } = result;
    
    if (context) {
      // Per-area actionable narrative sentences for the week
      const avgToPrefixW = (avg: number): string => avg > 25 ? '[g]' : avg > 5 ? '[m]' : '[c]';

      const getAreaWeekNarrative = (area: string, avg: number): string => {
        const tier = avg > 50 ? 0 : avg > 25 ? 1 : avg > 5 ? 2 : avg > -15 ? 3 : 4;
        const narratives: Record<string, string[]> = {
          Career: [
            'Strong career week — push forward on asks, negotiations, and making your work visible.',
            'Career has a good week ahead — act on professional opportunities and put asks forward.',
            'Career week is mixed — focus on existing commitments, hold off on major new moves.',
            'Career faces some friction this week — stay patient and see existing work through.',
            'Challenging career week — stay low-key and avoid fresh risks.',
          ],
          Wealth: [
            'Strong financial week — good for wealth decisions, investments, and income conversations.',
            'Wealth flows smoothly this week — good for financial tasks and raising money topics.',
            'Mixed wealth signals this week — routine finances ok, hold off on large commitments.',
            'Some financial resistance this week — defer major decisions where possible.',
            'Challenging financial week — hold off on large money moves or new commitments.',
          ],
          Health: [
            'Strong health week — good for wellness activities and sustained physical effort.',
            'Health is steady this week — maintain your routine and don\'t overextend.',
            'Mixed health week — protect your routine and avoid adding new stressors.',
            'Health needs care this week — reduce demands and rest where possible.',
            'Challenging health week — prioritise rest and recovery above new efforts.',
          ],
        };
        return narratives[area]?.[tier] ?? `${area}: check highlights below`;
      };

      const ALL_PRIORITY_AREAS_W = ['Career', 'Wealth', 'Health'];
      const areaWeekLines: string[] = [];
      ALL_PRIORITY_AREAS_W.forEach(area => {
        const aFeeds = weekFeeds.filter(f => (f.lifeArea === 'Self' ? 'Health' : f.lifeArea) === area);
        if (aFeeds.length === 0) return;
        const avg = aFeeds.reduce((s, f) => s + f.score, 0) / aFeeds.length;
        areaWeekLines.push(avgToPrefixW(avg) + getAreaWeekNarrative(area, avg));
        // Append organ watch only when Health is mixed or worse (avg ≤ 25)
        // avg > 25 = Good/Steady → no watch needed
        if (area === 'Health' && avg <= 25) {
          const activePlanets = [
            context.dashaContext.SD?.planet,
            context.dashaContext.PD?.planet,
          ].filter((p): p is PlanetName => !!p);
          const organLine = buildOrganWatchLine(activePlanets);
          if (organLine) areaWeekLines.push(`[n]⚕️ Watch: ${organLine}`);
        }
      });
      summary = areaWeekLines.length > 0
        ? areaWeekLines.join('\n')
        : '[n]Check highlights below for this week\'s outlook.';

      // Retrograde note for week — append as context line
      if (context.transitRetrogrades) {
        const retros = Object.entries(context.transitRetrogrades)
          .filter(([_, isR]) => isR).map(([p]) => p);
        if (retros.includes('Mercury')) summary += '\n[n]Mercury retrograde — avoid signing contracts this week.';
        else if (retros.includes('Saturn') || retros.includes('Jupiter')) {
          summary += `\n[n]${retros.includes('Saturn') ? 'Saturn' : 'Jupiter'} retrograde — best for review, not new starts.`;
        }
      }
    } else {
      // Fallback
      const positiveCount = weekFeeds.filter(f => f.score > 0).length;
      const negativeCount = weekFeeds.filter(f => f.score < 0).length;
      
      if (positiveCount > negativeCount * 1.5) {
        summary = `Promising week with ${positiveCount} opportunities emerging.`;
      } else if (negativeCount > positiveCount * 1.5) {
        summary = `Challenging week ahead. Plan carefully and stay grounded.`;
      } else {
        summary = 'Balanced week ahead with both opportunities and challenges.';
      }
    }
    
    predictions.push({
      id: 'pred-week',
      type: 'prediction',
      period: 'week',
      title: 'This Week\'s Outlook',
      summary,
      highlights: (() => {
        const PRIORITY_AREAS = ['Career', 'Wealth', 'Health'];
        const allAreas = [
          ...PRIORITY_AREAS.filter(a => weekFeeds.some(f => (f.lifeArea === 'Self' ? 'Health' : f.lifeArea) === a)),
          ...primaryAreas.filter(a => !PRIORITY_AREAS.includes(a))
        ].slice(0, 4);

        return allAreas.map(area => {
          const areaFeeds = weekFeeds.filter(f => (f.lifeArea === 'Self' ? 'Health' : f.lifeArea) === area);
          if (areaFeeds.length === 0) return null;
          const avgScore = areaFeeds.reduce((sum, f) => sum + f.score, 0) / areaFeeds.length;
          const areaLabel = area === 'Self' ? 'Health' : (area.charAt(0).toUpperCase() + area.slice(1));

          const weekChipActions: Record<string, string[]> = {
            Career: [
              'push forward on asks and negotiations',
              'act on opportunities, advance your agenda',
              'focus on existing work, hold major moves',
              'complete in-progress items first',
              'avoid new career initiatives',
            ],
            Wealth: [
              'strong for investments and financial decisions',
              'handle financial tasks, raise money topics',
              'routine finances ok, no large commitments',
              'defer major financial decisions',
              'hold off on new financial moves',
            ],
            Health: [
              'good for physical effort and wellness goals',
              'maintain routine, don\'t overextend',
              'protect routine, avoid adding new stressors',
              'rest and reduce demands this week',
              'prioritise rest and recovery',
            ],
          };
          const weekTier = avgScore > 50 ? 0 : avgScore > 25 ? 1 : avgScore > 5 ? 2 : avgScore > -15 ? 3 : 4;
          const weekActionList = weekChipActions[areaLabel] ?? ['act now', 'make progress', 'time your moves', 'proceed carefully', 'hold off'];
          const weekChipAction = weekActionList[weekTier];
          if (avgScore > 50) return `${areaLabel}: Excellent week — ${weekChipAction}`;
          if (avgScore > 25) return `${areaLabel}: Favourable — ${weekChipAction}`;
          if (avgScore > 5)  return `${areaLabel}: Mixed — ${weekChipAction}`;
          if (avgScore > -15) return `${areaLabel}: Low energy — ${weekChipAction}`;
          return `${areaLabel}: Challenging — ${weekChipAction}`;
        }).filter(Boolean) as string[];
      })()
    });
  }
  
  // This month's prediction
  const monthEnd = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const monthFeeds = feeds.filter(feed =>
    feed.window &&
    feed.window.start <= monthEnd &&
    feed.window.end >= now
  );
  
  if (monthFeeds.length > 0) {
    const result = calculatePredictionScore(monthFeeds, context || null, 'month');
    
    let summary = '';
    const { tone, primaryAreas } = result;
    
    if (context) {
      const keyAreas = ['Career', 'Wealth', 'Health']
        .filter(a => monthFeeds.some(f => (f.lifeArea === 'Self' ? 'Health' : f.lifeArea) === a))
        .concat(primaryAreas.filter(a => !['Career', 'Wealth', 'Health'].includes(a)))
        .slice(0, 2);
      const areaText = keyAreas.join(' and ');

      if (tone === 'very_favorable') {
        summary = areaText ? `High-potential month for ${areaText} — plan boldly.` : 'Excellent planetary conditions this month.';
      } else if (tone === 'favorable') {
        summary = areaText ? `Productive month for ${areaText} — steady progress expected.` : 'Good month overall. Use the momentum.';
      } else if (tone === 'mixed') {
        summary = areaText ? `Balanced month — ${areaText} have both opportunities and obstacles.` : 'Mixed month ahead. Identify the right windows.';
      } else if (tone === 'challenging') {
        summary = areaText ? `Challenging month for ${areaText} — strategic patience advised.` : 'Challenging month. Prioritise stability over growth.';
      } else {
        summary = areaText ? `Difficult month — conserve energy in ${areaText} areas.` : 'Difficult month ahead. Focus on what is essential.';
      }

      if (isEventTriggerPeriod(context.dashaContext)) {
        summary += ' High-significance period — events this month may have lasting impact.';
      }

      // Append organ watch only when Health avg score is mixed or worse (≤ 25)
      const healthMonthFeeds = monthFeeds.filter(f => (f.lifeArea === 'Self' ? 'Health' : f.lifeArea) === 'Health');
      if (healthMonthFeeds.length > 0) {
        const healthAvg = healthMonthFeeds.reduce((s, f) => s + f.score, 0) / healthMonthFeeds.length;
        if (healthAvg <= 25) {
          const activePlanets = [
            context.dashaContext.SD?.planet,
            context.dashaContext.PD?.planet,
          ].filter((p): p is PlanetName => !!p);
          const organLine = buildOrganWatchLine(activePlanets);
          if (organLine) summary += `\n[n]⚕️ Watch: ${organLine}`;
        }
      }
    } else {
      // Fallback
      const positiveCount = monthFeeds.filter(f => f.score > 0).length;
      const negativeCount = monthFeeds.filter(f => f.score < 0).length;
      
      if (positiveCount > negativeCount * 1.3) {
        summary = `Productive month ahead with ${positiveCount} positive planetary influences.`;
      } else if (negativeCount > positiveCount * 1.3) {
        summary = `Month requires patience and strategic planning. ${negativeCount} areas need attention.`;
      } else {
        summary = 'Month presents balanced energies. Focus on priorities and timing.';
      }
    }
    
    predictions.push({
      id: 'pred-month',
      type: 'prediction',
      period: 'month',
      title: 'This Month\'s Overview',
      summary,
      highlights: (() => {
        const PRIORITY_AREAS = ['Career', 'Wealth', 'Health'];
        const allAreas = [
          ...PRIORITY_AREAS.filter(a => monthFeeds.some(f => (f.lifeArea === 'Self' ? 'Health' : f.lifeArea) === a)),
          ...primaryAreas.filter(a => !PRIORITY_AREAS.includes(a))
        ].slice(0, 4);

        return allAreas.map(area => {
          const areaFeeds = monthFeeds.filter(f => (f.lifeArea === 'Self' ? 'Health' : f.lifeArea) === area);
          if (areaFeeds.length === 0) return null;
          const avgScore = areaFeeds.reduce((sum, f) => sum + f.score, 0) / areaFeeds.length;
          const count = areaFeeds.length;
          const areaLabel = area === 'Self' ? 'Health' : (area.charAt(0).toUpperCase() + area.slice(1));

          if (avgScore > 40) return `${areaLabel}: High-potential month (${count} windows)`;
          if (avgScore > 20) return `${areaLabel}: Generally positive (${count} windows)`;
          if (avgScore > 5)  return `${areaLabel}: Mixed — use good windows`;
          return `${areaLabel}: Needs careful management`;
        }).filter(Boolean) as string[];
      })()
    });
  }
  
  return predictions;
}
